/**
 * Head tags for the React app's public pages. The build writes one static
 * HTML file per route with these tags (vite.brand.ts → scripts/seo-pages.mjs),
 * so crawlers and link previews (WhatsApp, LinkedIn, X) see them without
 * JavaScript; <SeoHead> keeps the same title and description after client-side
 * navigation. Server-rendered pages build theirs in rai.site (seo.py); the
 * Organization and WebSite JSON-LD below mirror rai.site.seo.
 *
 * No React here: the build loads this module on its own.
 */
import { brand } from "@/brand";

import { faqFor, NONE, raFaqFor, servicesFaqFor, topFaqFor, trialFaqFor, type FaqItem, type FaqTrials } from "./faq";

/**
 * The membership's monthly price in rupees, GST included, for the /pricing
 * page's title and Offer structured data. The page itself reads the live
 * price from /platform/plans; the static head can't, so keep this in step with
 * the default plan (db/migrations/V052__single_user_type.sql) when an admin
 * changes the price.
 */
export const MEMBERSHIP_PRICE_INR = 4999;
const PRICE = `₹${MEMBERSHIP_PRICE_INR.toLocaleString("en-IN")}`;

export interface SeoPage {
  path: string;
  /** Full <title>, at most 60 characters. */
  title: string;
  /** Meta description, 50–155 characters. */
  description: string;
  /** Breadcrumb name (inner pages). */
  crumb?: string;
  /** Share image under /og/ (default.png when absent). */
  image?: string;
  /** Questions for the page's FAQPage JSON-LD (from faq.ts; shown on the page too). */
  faq?: FaqItem[];
  /** Home: Organization and WebSite JSON-LD. */
  home?: boolean;
  /** Pricing: the membership as a SoftwareApplication with its Offer. */
  offer?: boolean;
}

/** "Subject | Brand", dropping the brand when the whole would pass 60 characters. */
export function seoTitle(subject: string): string {
  const full = `${subject} | ${brand.name}`;
  return full.length <= 60 ? full : subject;
}

/**
 * The pages' head tags, following the free-trial switches (Admin → Settings →
 * Sign-up & trial; days or null). The build writes SEO_PAGES, which promise no
 * trial, so a static head is never wrong; <SeoHead> adds the trial wording in
 * the browser while a switch is on.
 */
export function seoPagesFor(t: FaqTrials): SeoPage[] {
  const m = t.member;
  return [
    {
      path: "/",
      title: seoTitle("Stock Screener & Option Strategy Builder"),
      description: `Stock screener, option strategy builder, backtests, option chain, F&O dashboards and alerts for NSE, BSE and MCX. ${m ? `Start your ${m}-day free trial.` : "One membership, every tool."}`,
      home: true,
      faq: topFaqFor(t),
    },
    {
      path: "/free-trial",
      title: seoTitle(m ? `${m}-Day Free Trial` : "Free Trial"),
      description: `Try ${brand.name} free${m ? ` for ${m} days` : ""}: every research tool, from strategy labs and the screener to F&O dashboards and alerts. No payment needed to start.`,
      crumb: "Free trial",
      image: "free-trial.png",
      faq: trialFaqFor(m),
    },
    {
      path: "/pricing",
      title: seoTitle(`Pricing: ${PRICE}/month, ${m ? `${m}-Day Free Trial` : "Every Tool Included"}`),
      description: `One membership, every research tool: stock screener, option strategy builder, backtests and alerts. ${PRICE}/month incl. GST${m ? ` after a ${m}-day free trial` : ""}.`,
      crumb: "Pricing",
      offer: true,
    },
    {
      path: "/for-research-analysts",
      title: seoTitle("For SEBI Research Analysts: Publish Calls"),
      description: `SEBI-registered Research Analysts publish calls with automatic disclosures, invite clients free and keep a verified track record.${t.ra ? ` ${t.ra}-day free trial.` : ""}`,
      crumb: "For Research Analysts",
      faq: raFaqFor(t),
    },
    {
      path: "/faq",
      title: seoTitle("Frequently Asked Questions"),
      description: `Answers on the research tools members get, pricing, data sources, backtests, the RA and IA directories and ${m ? `the ${m}-day free trial` : "how membership works"}.`,
      crumb: "FAQ",
      faq: faqFor(t),
    },
    {
      path: "/services",
      title: seoTitle("Research Platform, Apps and APIs"),
      description:
        "The research platform for investors and traders, plus custom apps, research and analytics APIs and backtests for Indian markets.",
      crumb: "Services",
      faq: servicesFaqFor(m),
    },
    {
      path: "/find-research-analyst",
      title: seoTitle("Find a SEBI Registered Research Analyst Near Me"),
      description:
        "Search every SEBI-registered Research Analyst by name, city, state or PIN code. Check registration numbers and validity before you pay for research.",
      crumb: "Find a Research Analyst",
    },
    {
      path: "/find-investment-adviser",
      title: seoTitle("Find a SEBI Registered Investment Adviser Near Me"),
      description:
        "Search SEBI-registered Investment Advisers by name, INA number, city, state or PIN code, listed from SEBI's public register. Confirm before you pay.",
      crumb: "Find an Investment Adviser",
    },
    {
      path: "/about",
      title: seoTitle("About Us"),
      description: `${brand.name} gives investors and traders professional research tools for Indian markets. Operated by ${brand.legalName || brand.name}.`,
      crumb: "About us",
    },
    {
      path: "/contact",
      title: seoTitle("Contact Us"),
      description: `How to reach ${brand.name}: email, phone, office address in ${brand.city || "India"} and the grievance officer for complaints.`,
      crumb: "Contact us",
    },
    {
      path: "/terms",
      title: seoTitle("Terms and Conditions"),
      description: `The terms for using ${brand.name}: accounts, the free trial and membership, acceptable use, market data and backtests, and how disputes are handled.`,
      crumb: "Terms and conditions",
    },
    {
      path: "/privacy",
      title: seoTitle("Privacy Policy"),
      description: `What personal data ${brand.name} collects, why, who we share it with, how long we keep it and your rights under India's data protection law.`,
      crumb: "Privacy policy",
    },
    {
      path: "/refund-policy",
      title: seoTitle("Cancellation and Refund Policy"),
      description: `How to cancel a ${brand.name} membership, what happens to your access after the free trial, and when and how you get money back.`,
      crumb: "Cancellation and refunds",
    },
    {
      path: "/delivery-policy",
      title: seoTitle("Service Delivery Policy"),
      description: `How and when you get access to what you pay for on ${brand.name}: a digital service, when your membership starts and what to do if it doesn't.`,
      crumb: "Service delivery",
    },
    {
      path: "/disclaimer",
      title: seoTitle("Disclaimer and Risk Disclosure"),
      description: `Important information about the market data, tools and backtests on ${brand.name}: market risks, no investment advice, data limits and the directories.`,
      crumb: "Disclaimer",
    },
  ];
}

/** The build's head tags: no trial promised (see seoPagesFor). */
export const SEO_PAGES: SeoPage[] = seoPagesFor(NONE);

export function seoPage(path: string, trials: FaqTrials = NONE): SeoPage | undefined {
  const p = path.length > 1 ? path.replace(/\/+$/, "") : path;
  return seoPagesFor(trials).find((s) => s.path === p);
}

/** https://domain, no trailing slash (brand.json "domain"). */
export function siteOrigin(): string {
  const d = brand.domain || "localhost";
  return (d.startsWith("http") ? d : `https://${d}`).replace(/\/$/, "");
}

export function canonicalUrl(path: string, origin = siteOrigin()): string {
  return path === "/" ? `${origin}/` : `${origin}${path}`;
}

type Json = Record<string, unknown>;

function organization(origin: string): Json {
  const org: Json = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": `${origin}/#organization`,
    name: brand.legalName || brand.name,
    url: `${origin}/`,
    logo: `${origin}/logo-512.png`,
    image: `${origin}/og/default.png`,
    description: brand.description || brand.tagline,
  };
  if (brand.legalName && brand.legalName !== brand.name) org.alternateName = brand.name;
  if (brand.supportEmail) org.email = brand.supportEmail;
  if (brand.phone) org.telephone = brand.phone;
  if (brand.supportEmail || brand.phone) {
    org.contactPoint = [
      {
        "@type": "ContactPoint",
        contactType: "customer support",
        areaServed: "IN",
        availableLanguage: ["en", "hi"],
        ...(brand.supportEmail ? { email: brand.supportEmail } : {}),
        ...(brand.phone ? { telephone: brand.phone } : {}),
      },
    ];
  }
  // Locality and region only: the street address is kept for tax receipts.
  if (brand.city || brand.region) {
    org.address = {
      "@type": "PostalAddress",
      ...(brand.city ? { addressLocality: brand.city } : {}),
      ...(brand.region ? { addressRegion: brand.region } : {}),
      addressCountry: "IN",
    };
  }
  if (brand.sameAs?.length) org.sameAs = brand.sameAs;
  return org;
}

function website(origin: string): Json {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": `${origin}/#website`,
    name: brand.name,
    url: `${origin}/`,
    inLanguage: "en-IN",
    publisher: { "@id": `${origin}/#organization` },
  };
}

/**
 * The membership on /pricing: a web app with one monthly Offer. Google's
 * SoftwareApplication rich result also wants ratings, which we don't have and
 * won't invent; the Offer still states the price and currency.
 */
function membershipOffer(page: SeoPage, origin: string): Json {
  const price = MEMBERSHIP_PRICE_INR.toFixed(2);
  return {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name: brand.name,
    url: `${origin}/`,
    description: brand.description || brand.tagline,
    applicationCategory: "FinanceApplication",
    operatingSystem: "Web",
    publisher: { "@id": `${origin}/#organization` },
    offers: {
      "@type": "Offer",
      name: "Membership",
      url: canonicalUrl(page.path, origin),
      price,
      priceCurrency: "INR",
      availability: "https://schema.org/InStock",
      category: "subscription",
      priceSpecification: {
        "@type": "UnitPriceSpecification",
        price,
        priceCurrency: "INR",
        valueAddedTaxIncluded: true,
        billingDuration: "P1M",
        unitCode: "MON",
      },
    },
  };
}

/** JSON-LD objects for a page. */
export function seoJsonLd(page: SeoPage, origin = siteOrigin()): Json[] {
  const out: Json[] = [];
  if (page.home) out.push(organization(origin), website(origin));
  if (page.crumb) {
    out.push({
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: `${origin}/` },
        { "@type": "ListItem", position: 2, name: page.crumb, item: canonicalUrl(page.path, origin) },
      ],
    });
  }
  if (page.offer) out.push(membershipOffer(page, origin));
  if (page.faq?.length) {
    out.push({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: page.faq.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
    });
  }
  return out;
}

function esc(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
}

/** The head tags for a public page (title included), as HTML. */
export function seoHeadHtml(page: SeoPage, origin = siteOrigin()): string {
  const url = canonicalUrl(page.path, origin);
  const image = `${origin}/og/${page.image ?? "default.png"}`;
  const meta = (attr: string, key: string, content: string) => `<meta ${attr}="${key}" content="${esc(content)}">`;
  const ld = seoJsonLd(page, origin);
  const body = ld.length === 1 ? ld[0] : { "@context": "https://schema.org", "@graph": ld };
  return [
    `<title>${esc(page.title)}</title>`,
    meta("name", "description", page.description),
    `<link rel="canonical" href="${esc(url)}">`,
    `<link rel="alternate" hreflang="en-IN" href="${esc(url)}">`,
    `<link rel="alternate" hreflang="x-default" href="${esc(url)}">`,
    meta("name", "robots", "index, follow, max-image-preview:large"),
    meta("property", "og:type", "website"),
    meta("property", "og:site_name", brand.name),
    meta("property", "og:title", page.title),
    meta("property", "og:description", page.description),
    meta("property", "og:url", url),
    meta("property", "og:image", image),
    meta("property", "og:image:width", "1200"),
    meta("property", "og:image:height", "630"),
    meta("property", "og:image:alt", brand.name),
    meta("property", "og:locale", "en_IN"),
    meta("name", "twitter:card", "summary_large_image"),
    meta("name", "twitter:title", page.title),
    meta("name", "twitter:description", page.description),
    meta("name", "twitter:image", image),
    `<script type="application/ld+json">${JSON.stringify(body).replace(/</g, "\\u003c")}</script>`,
  ].join("\n    ");
}
